import { Component, OnInit, Output, EventEmitter, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ToastService } from '../../core/services/toast.service';
import { CommonModule } from '@angular/common';
import { LoginRegisterTabsComponent, TabOption } from '../login-register-tabs';
import { UserDetailsModel } from '../../models/user-details.model';
import { BaseService } from '../../core/services/base.service';

import { BookingDetailsModel } from '../../models/booking-details.model';
import { countryModel } from '../../models/country-code.model';
import { TripSearchService } from '../../core/services/trip-search.service';
import { ClientDetails } from '../../models/client-details.model';
import { AuthService } from '../../core/services/auth.service';
import { User } from '../../core/interfaces/user.interface';
import { ResponseModel } from '../../models/response.model';
import { CustomerDetails, OtpLoginResponse } from '../../models/customer.model';

type FormMode = 'login' | 'register' | 'otpLogin' | 'verifyOtp' | 'forgotPassword' | 'forgotPasswordOtp' | 'forgotPasswordStep2';

@Component({
  selector: 'app-login-register-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    LoginRegisterTabsComponent
  ],
  templateUrl: './login-register-form.html',
  styleUrl: './login-register-form.scss'
})
export class LoginRegisterForm implements OnInit {
  @Output() switchToOtp = new EventEmitter<void>();
  @Output() switchToForgotPassword = new EventEmitter<void>();
  @Output() loginSuccess = new EventEmitter<void>();

  formMode: FormMode = 'login';
  loginForm: FormGroup;
  registerForm: FormGroup;
  otpLoginForm: FormGroup;
  verifyOtpForm: FormGroup;
  forgotPasswordForm: FormGroup;
  forgotPasswordOtpForm: FormGroup;
  forgotPasswordStep2Form: FormGroup;
  isLoading = false;
  showPassword = false;
  showRegisterPassword = false;
  showConfirmPassword = false;
  showForgotPasswordConfirmPassword = false;
  userDetails!: UserDetailsModel;
  bookingDetailsModels!: BookingDetailsModel;
  bindcountryCodeDropDown: countryModel[] = [];
  countryModel: countryModel[] = [];
  clientId: number = 0;

  // Inject AuthService
  private readonly authService = inject(AuthService);

  // Tab options for login/register only
  loginRegisterOptions: TabOption[] = [
    { value: 'login', label: 'Login', ariaLabel: 'Login tab' },
    { value: 'register', label: 'Sign Up', ariaLabel: 'Register tab' }
  ];

  // Tab options for forgot password
  forgotPasswordOptions: TabOption[] = [
    { value: 'forgotPassword', label: 'Forgot Password', ariaLabel: 'Forgot Password tab' }
  ];


  constructor(
    private fb: FormBuilder,
    public myFleetService: BaseService,
    private toastService: ToastService,
    private tripSearchService: TripSearchService,
    private cdr: ChangeDetectorRef
  ) {
    this.loginForm = this.fb.group({
      mobile: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });

    this.registerForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      countryCode: [91, [Validators.required]], // Default to India (+91)
      mobile: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    this.otpLoginForm = this.fb.group({
      countryCode: [91, [Validators.required]], // Default to India (+91)
      mobile: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]]
    });

    this.verifyOtpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^[0-9]{4}$/)]]
    });

    this.forgotPasswordForm = this.fb.group({
      mobile: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]]
    });

    this.forgotPasswordOtpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^[0-9]{4}$/)]]
    });

    this.forgotPasswordStep2Form = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit() {
    // Initialize form validation
    this.bindCountryCode();
    // Defer setting default country code to avoid change detection error
    setTimeout(() => {
      this.registerForm.get('countryCode')?.setValue('+91');
      this.otpLoginForm.get('countryCode')?.setValue('+91');
    });
  }

  onTabChange(tab: string) {
    if (tab === 'login' || tab === 'register') {
      if (this.formMode === 'otpLogin') {
        // If we're in OTP mode and user clicks register, switch to register form
        if (tab === 'register') {
          this.formMode = 'register';
        }
        // If user clicks login while in OTP mode, stay in OTP mode (already correct)
      } else {
        this.formMode = tab as FormMode;
      }
    } else if (tab === 'forgotPassword') {
      // Handle forgot password tab - stay in forgot password mode
      this.formMode = 'forgotPassword';
    }
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleRegisterPasswordVisibility() {
    this.showRegisterPassword = !this.showRegisterPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  toggleForgotPasswordConfirmPasswordVisibility() {
    this.showForgotPasswordConfirmPassword = !this.showForgotPasswordConfirmPassword;
  }

  isFieldInvalid(fieldName: string): boolean {

    const form = this.getCurrentForm();
    const field = form.get(fieldName);
    // Only show errors when field is invalid AND has been interacted with AND has a value
    return field ? (field.invalid && (field.dirty || field.touched || field.value == '')) : false;
  }

  getFieldError(fieldName: string): string {
    const form = this.getCurrentForm();
    const field = form.get(fieldName);

    if (!field || !field.errors) return '';

    if (field.errors['required']) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
    }

    if (field.errors['email']) {
      return 'Please enter a valid email address';
    }

    if (field.errors['pattern']) {
      if (fieldName === 'mobile') {
        return 'Please enter a valid 10-digit mobile number';
      }
      if (fieldName === 'otp') {
        return 'Please enter a valid 4-digit OTP';
      }
    }

    if (field.errors['minlength']) {
      const requiredLength = field.errors['minlength'].requiredLength;
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} must be at least ${requiredLength} characters`;
    }

    if (field.errors['passwordMismatch']) {
      return 'Passwords do not match';
    }

    return 'Invalid input';
  }

  getCurrentForm(): FormGroup {
    switch (this.formMode) {
      case 'login':
        return this.loginForm;
      case 'register':
        return this.registerForm;
      case 'otpLogin':
        return this.otpLoginForm;
      case 'verifyOtp':
        return this.verifyOtpForm;
      case 'forgotPassword':
        return this.forgotPasswordForm;
      case 'forgotPasswordOtp':
        return this.forgotPasswordOtpForm;
      case 'forgotPasswordStep2':
        return this.forgotPasswordStep2Form;
      default:
        return this.loginForm;
    }
  }

  isFormValid(): boolean {
    const form = this.getCurrentForm();
    return form.valid;
  }

  passwordMatchValidator(group: FormGroup) {
    const password = group.get('password');
    const confirmPassword = group.get('confirmPassword');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      if (confirmPassword) {
        confirmPassword.setErrors(null);
      }
      return null;
    }
  }

  onSubmit() {
    debugger;
    switch (this.formMode) {
      case 'login':
        this.onLoginSubmit();
        break;
      case 'register':
        this.onRegisterSubmit();
        break;
      case 'otpLogin':
        //this.onOtpLoginSubmit();
        this.sendOTP();
        break;
      case 'verifyOtp':
        this.onVerifyOtpSubmit();
        break;
      case 'forgotPassword':
        this.onForgotPasswordSubmit();
        break;
      case 'forgotPasswordOtp':
        this.onForgotPasswordOtpSubmit();
        break;
      case 'forgotPasswordStep2':
        this.onForgotPasswordStep2Submit();
        break;
    }
  }

  // Add this method after the existing methods
  resetLoadingState(): void {
    console.log('Login: Manually resetting loading state');
    this.isLoading = false;
    this.cdr.detectChanges(); // Force change detection
  }

  onLoginSubmit() {
    if (this.loginForm.valid) {
      // Map form values to API expected format
      const formValues = this.loginForm.value;
      const loginData = {
        mobileNumber: formValues.mobile, // Map 'mobile' to 'mobileNumber'
        password: formValues.password
      };

      const apiUrl = '/api/B2COnlineBooking/CustomerLogin';
      this.isLoading = true;
      this.cdr.detectChanges(); // Force change detection
      console.log('Login: Starting login process, isLoading set to true');

      // Safety timeout to prevent infinite loading
      const loadingTimeout = setTimeout(() => {
        if (this.isLoading) {
          console.warn('Login request timed out, resetting loading state');
          this.resetLoadingState();
          this.toastService.error('Request timed out. Please try again.');
        }
      }, 30000); // 30 seconds timeout

      this.myFleetService.post(apiUrl, loginData).subscribe({
        next: (res: any) => {
          // Clear the timeout since request completed
          clearTimeout(loadingTimeout);

          if (res['responseCode'] === 'OK') {
            let customerId = res['data'].customerId;
            const apiUrl1 = `/api/B2COnlineBooking/${customerId}/GetCustomerDetails`;

            this.myFleetService.getData(apiUrl1).subscribe({
              next: (customerRes: any) => {
                if (customerRes['responseCode'] === 'OK') {
                  // Create user object for AuthService
                  const userData = customerRes['data'];
                  const user: User = {
                    id: userData.customerId || userData.id,
                    email: userData.emailAddress || '',
                    firstName: userData.name ? userData.name.split(' ')[0] : '',
                    lastName: userData.name ? userData.name.split(' ').slice(1).join(' ') : '',
                    phone: userData.mobileNumber || userData.phone,
                    isVerified: true,
                    createdAt: new Date(userData.createdAt || Date.now()),
                    updatedAt: new Date(userData.updatedAt || Date.now()),
                    token: userData.token || ''
                  };

                  // Store in AuthService
                  this.authService.handleSuccessfulLogin(user);

                  this.loginForm.reset();

                  // Reset loading state
                  this.resetLoadingState();
                  console.log('Login: Success - isLoading set to false');

                  // Show success message
                  this.toastService.success('Login successful!');

                  // Emit login success event to parent component
                  this.loginSuccess.emit();
                } else {
                  // Handle customer details API error
                  console.error('Login: Customer details API error:', customerRes);
                  this.resetLoadingState();
                  console.log('Login: Customer details error - isLoading set to false');
                  this.toastService.error(customerRes['responseMessage'] || 'Error fetching user details');
                }
              },
              error: (customerError: any) => {
                console.error('Login: Error fetching customer details:', customerError);
                this.resetLoadingState();
                console.log('Login: Customer details error - isLoading set to false');
                this.toastService.error('Error fetching user details');
              }
            });
          } else {
            console.error('Login: Login failed:', res['responseMessage']);
            this.resetLoadingState();
            console.log('Login: Login failed - isLoading set to false');
            this.toastService.error(res['responseMessage'] || 'Login failed');
          }
        },
        error: (error: any) => {
          // Clear the timeout since request completed
          clearTimeout(loadingTimeout);

          console.error('Login: API error:', error);
          this.resetLoadingState();
          console.log('Login: API error - isLoading set to false');

          let errorMessage = 'Login failed. Please try again.';
          if (error.error && error.error.responseMessage) {
            errorMessage = error.error.responseMessage;
          } else if (error.message) {
            errorMessage = error.message;
          }

          this.toastService.error(errorMessage);
        },
        complete: () => {
          // Ensure loading is reset even if something goes wrong
          console.log('Login: Request completed, ensuring isLoading is false');
          this.resetLoadingState();
        }
      });
    } else {
      this.markFormGroupTouched(this.loginForm);
    }
  }

  bindCountryCode() {
    const apiUrl = '/api/B2COnlineBooking/GetCountry';
    this.myFleetService.postDataWithoutParamter(apiUrl).subscribe({
      next: (res: any) => {

        if (res['responseCode'] == 'OK') {
          this.bindcountryCodeDropDown = res['data'] as countryModel[];
          // Sort countries by country code in ascending order
          this.bindcountryCodeDropDown.sort((a, b) => a.countryCode - b.countryCode);
          this.countryModel = this.bindcountryCodeDropDown;
        }
      },
      error: (error: any) => {
        console.error('Error fetching country code:', error);
        this.toastService.error(error.error.responseMessage || 'Error fetching country code');
      }
    });
  }

  getCountryDisplayValue(countryCode: number): string {
    const country = this.bindcountryCodeDropDown.find(c => c.countryCode === countryCode);
    return country ? country.countryCode.toString() : countryCode.toString();
  }

  displayCountryCode = (countryCode: number): string => {
    return countryCode ? `${countryCode}` : '';
  }

  onCountryCodeChange() {
    let selectedCountryCode: any;

    if (this.formMode === 'register') {
      selectedCountryCode = this.registerForm.get('countryCode')?.value;
    } else if (this.formMode === 'otpLogin') {
      selectedCountryCode = this.otpLoginForm.get('countryCode')?.value;
    }

    console.log('Selected Country Code:', selectedCountryCode);
    console.log('Selected Country Code with +:', `+${selectedCountryCode}`);
  }

  sendOTP() {
    debugger;
    if (true) {
      const formValues = this.registerForm.value;
      const LoginOtp = this.otpLoginForm.value;

      this.tripSearchService.loadClientDetails().subscribe({
        next: (clientDetails: ClientDetails) => {
          if (clientDetails.clientId !== null) {
            this.clientId = clientDetails.clientId;
            debugger
            
            let otpData: any;
            const isSignupFlow = this.formMode === 'register' || this.registerForm.dirty;
            if (isSignupFlow) {
              otpData = {
                name: formValues.name,
                emailAddress: formValues.email,
                mobileNumber: formValues.mobile,
                password: formValues.password,
                countryCode: formValues.countryCode,
                accessFrom: 'SignUp',
                clientId: this.clientId
              };
            } else {
              otpData = {
                emailAddress: formValues.email,
                mobileNumber: LoginOtp.mobile,
                accessFrom: 'LogInOtp',
                clientId: this.clientId
              };
            }

            const apiUrl = '/api/B2COnlineBooking/B2CCustomerRegistration';

            this.isLoading = true;

            this.myFleetService.post(apiUrl, otpData).subscribe({
              next: (res: any) => {
                debugger;
                this.isLoading = false;
                console.log('OTP API response:', res);
                if (res['responseCode'] == 'OK') {
                  console.log('OTP response data:', res['data']);

                  // Check if data exists
                  if (res['data']) {
                    this.userDetails = res['data'] as UserDetailsModel;
                    console.log('Client ID after OTP sent:', this.clientId);
                    console.log('User details stored:', this.userDetails);
                    localStorage.setItem('otpLoginMobile', formValues.mobile);
                    // Switch to OTP verification form immediately
                    this.formMode = 'verifyOtp';
                    this.cdr.detectChanges(); // Force immediate UI update
                    this.toastService.success('OTP sent successfully!');
                  } else {
                    console.error('OTP response OK but no data received');
                    this.toastService.error('OTP sent but response data is incomplete. Please try again.');
                  }
                } else {
                  this.toastService.error(res['responseMessage'] || 'Failed to send OTP');
                }
              },
              error: (err: any) => {
                this.isLoading = false;
                console.error('Error sending OTP:', err);
                this.toastService.error(err.error?.responseMessage || 'Failed to send OTP');
              }
            });
          } else {
            this.toastService.error('Failed to load client details. Please try again.');
          }
        },
        error: (error: any) => {
          console.error('Error loading client details:', error);
          this.toastService.error('Failed to load client details. Please try again.');
        }
      });
    } else {
      this.markFormGroupTouched(this.registerForm);
    }
  }

  onRegisterSubmit() {
    debugger;
    if (this.registerForm.valid) {

      this.sendOTP();
    } else {
      this.markFormGroupTouched(this.registerForm);
    }
  }

  onOtpLoginSubmit() {
    if (this.otpLoginForm.valid) {
      const formValues = this.otpLoginForm.value;
      const mobileNumber = formValues.mobile;

      this.isLoading = true;
      this.cdr.detectChanges(); // Force change detection
      console.log('OTP Login: Starting OTP login process for mobile:', mobileNumber);

      // Safety timeout to prevent infinite loading
      const loadingTimeout = setTimeout(() => {
        if (this.isLoading) {
          console.warn('OTP login request timed out, resetting loading state');
          this.resetLoadingState();
          this.toastService.error('Request timed out. Please try again.');
        }
      }, 10000); // 10 seconds timeout

      this.authService.otpLogin(mobileNumber).subscribe({
        next: (res: ResponseModel) => {
          // Clear the timeout since request completed
          clearTimeout(loadingTimeout);
          console.log('OTP Login: API response received:', res);

          if (res.responseCode === 'OK') {
            this.resetLoadingState();
            console.log('OTP Login: Success - isLoading set to false');
            this.toastService.success('OTP sent successfully!');

            // Store mobile number for OTP verification
            localStorage.setItem('otpLoginMobile', mobileNumber);

            // Switch to verify OTP form
            this.formMode = 'verifyOtp';
            this.cdr.detectChanges(); // Force change detection to update UI immediately
          } else {
            console.error('OTP Login: Failed:', res.responseMessage);
            this.resetLoadingState();
            console.log('OTP Login: Failed - isLoading set to false');
            this.toastService.error(res.responseMessage || 'Failed to send OTP');
          }
        },
        error: (error: any) => {
          // Clear the timeout since request completed
          clearTimeout(loadingTimeout);

          console.error('OTP Login: API error:', error);
          this.resetLoadingState();
          console.log('OTP Login: API error - isLoading set to false');

          let errorMessage = 'Failed to send OTP. Please try again.';
          if (error.error && error.error.responseMessage) {
            errorMessage = error.error.responseMessage;
          } else if (error.message) {
            errorMessage = error.message;
          }

          this.toastService.error(errorMessage);
        },
        complete: () => {
          // Ensure loading is reset even if something goes wrong
          console.log('OTP Login: Request completed, ensuring isLoading is false');
          this.resetLoadingState();
        }
      });
    } else {
      this.markFormGroupTouched(this.otpLoginForm);
    }
  }

  onVerifyOtpSubmit() {
    debugger;

    if (this.verifyOtpForm.valid) {
      const formValuesOtp = this.verifyOtpForm.value;
      const otpValue = formValuesOtp.otp;

      const formSignUp = this.registerForm.value;

      const isSignupFlow = this.formMode === 'register' || this.registerForm.dirty;
      let mobileNumber: any;
      if (isSignupFlow) {
        mobileNumber = localStorage.getItem('otpLoginMobile');
      } else {
        mobileNumber = this.otpLoginForm.value;
      }



      if (!mobileNumber) {
        this.toastService.error('Mobile number not found. Please try the OTP login process again.');
        this.formMode = 'otpLogin';
        this.cdr.detectChanges(); // Force change detection to update UI immediately
        return;
      }

      this.isLoading = true;
      this.cdr.detectChanges(); // Force change detection
      console.log('OTP Verification: Verifying OTP for mobile:', mobileNumber);

      // Safety timeout to prevent infinite loading
      const loadingTimeout = setTimeout(() => {
        if (this.isLoading) {
          console.warn('OTP verification request timed out, resetting loading state');
          this.resetLoadingState();
          this.toastService.error('Request timed out. Please try again.');
        }
      }, 30000); // 30 seconds timeout

      // Load client details and verify OTP
      this.tripSearchService.loadClientDetails().subscribe({
        next: (clientDetails: ClientDetails) => {
          if (clientDetails.clientId !== null) {
            this.clientId = clientDetails.clientId;
            debugger;

            // Call OTP verification API for login

            if (isSignupFlow) {
              this.authService.IsSignUp(formSignUp.mobile, formSignUp.name, this.clientId, formSignUp.email, formSignUp.password, formSignUp.address, formSignUp.gstNumber, otpValue, formSignUp.countryCode).subscribe({
                next: (res: ResponseModel) => {
                  debugger;
                  // Clear the timeout since request completed
                  clearTimeout(loadingTimeout);
                  console.log('OTP Verification: API response received:', res);

                  if (res.responseCode === 'OK') {
                    // Extract customer ID from response
                    const otpLoginData = res.data as OtpLoginResponse;
                    const customerId = Number(otpLoginData);

                    if (customerId) {
                      // Get customer details using customer ID
                      this.authService.getCustomerDetails(customerId).subscribe({
                        next: (customerRes: ResponseModel) => {
                          console.log('Customer Details: API response received:', customerRes);

                          if (customerRes.responseCode === 'OK') {
                            const customerData = customerRes.data as CustomerDetails;

                            // Create user object for AuthService
                            const user: User = {
                              id: customerData.customerId.toString(),
                              email: customerData.emailAddress || '',
                              firstName: customerData.name ? customerData.name.split(' ')[0] : '',
                              lastName: customerData.name ? customerData.name.split(' ').slice(1).join(' ') : '',
                              phone: customerData.mobileNumber.toString(),
                              isVerified: true,
                              createdAt: new Date(),
                              updatedAt: new Date(),
                              token: '' // No token in this flow
                            };

                            // Store in AuthService with client details
                            this.authService.handleSuccessfulLoginWithClientDetails(user, clientDetails);

                            // Store additional customer details in storage for session management
                            localStorage.setItem('customerDetails', JSON.stringify(customerData));
                            localStorage.setItem('otpLoginResponse', JSON.stringify(otpLoginData));

                            this.resetLoadingState();
                            console.log('OTP Verification: Success - isLoading set to false');
                            this.toastService.success('Login successful!');

                            // Clear stored mobile number
                            localStorage.removeItem('otpLoginMobile');

                            // Emit login success event
                            this.loginSuccess.emit();
                          } else {
                            console.error('Customer Details: Failed:', customerRes.responseMessage);
                            this.resetLoadingState();
                            this.toastService.error(customerRes.responseMessage || 'Failed to get customer details');
                          }
                        },
                        error: (customerError: any) => {
                          console.error('Customer Details: API error:', customerError);
                          this.resetLoadingState();

                          let errorMessage = 'Failed to get customer details. Please try again.';
                          if (customerError.error && customerError.error.responseMessage) {
                            errorMessage = customerError.error.responseMessage;
                          } else if (customerError.message) {
                            errorMessage = customerError.message;
                          }

                          this.toastService.error(errorMessage);
                        }
                      });
                    } else {
                      console.error('OTP Verification: No customer ID in response');
                      this.resetLoadingState();
                      this.toastService.error('Login successful but customer ID not found. Please contact support.');
                    }
                  } else {
                    console.error('OTP Verification: Failed:', res.responseMessage);
                    this.resetLoadingState();
                    console.log('OTP Verification: Failed - isLoading set to false');
                    this.toastService.error(res.responseMessage || 'OTP verification failed');
                  }
                },
                error: (error: any) => {
                  debugger;
                  clearTimeout(loadingTimeout);
                  this.resetLoadingState();

                  if (error.error.responseCode == 'BadRequest') {
                    this.toastService.error(error.error.responseMessage);
                    this.formMode = 'verifyOtp';
                    this.cdr.detectChanges();
                    return;
                  }
                },
                complete: () => {
                  // Ensure loading is reset even if something goes wrong
                  console.log('OTP Verification: Request completed, ensuring isLoading is false');
                  if (this.isLoading) {
                    this.isLoading = false;
                    this.cdr.detectChanges(); // Force change detection
                  }
                }
              });
            } else {
              this.authService.verifyOtpForLogin(mobileNumber.mobile, otpValue, this.clientId).subscribe({
                next: (res: ResponseModel) => {

                  if (res.responseCode === 'OK') {
                    debugger;
                    this.toastService.success(res.responseMessage);
                    const userData = res['data'];
                    console.log(userData.customerId);
                    this.authService.getCustomerDetails(userData.customerId).subscribe({
                      next: (customerRes: ResponseModel) => {
                        console.log('Customer Details: API response received:', customerRes);

                        if (customerRes.responseCode === 'OK') {
                          const customerData = customerRes.data as CustomerDetails;

                          // Create user object for AuthService
                          const user: User = {
                            id: customerData.customerId.toString(),
                            email: customerData.emailAddress || '',
                            firstName: customerData.name ? customerData.name.split(' ')[0] : '',
                            lastName: customerData.name ? customerData.name.split(' ').slice(1).join(' ') : '',
                            phone: customerData.mobileNumber.toString(),
                            isVerified: true,
                            createdAt: new Date(),
                            updatedAt: new Date(),
                            token: '' // No token in this flow
                          };

                          // Store in AuthService with client details
                          this.authService.handleSuccessfulLoginWithClientDetails(user, clientDetails);

                          // Store additional customer details in storage for session management
                          localStorage.setItem('customerDetails', JSON.stringify(customerData));
                          //localStorage.setItem('otpLoginResponse', JSON.stringify(otpLoginData));

                          this.resetLoadingState();
                          console.log('OTP Verification: Success - isLoading set to false');
                          

                          // Clear stored mobile number
                          localStorage.removeItem('otpLoginMobile');

                          // Emit login success event
                          this.loginSuccess.emit();
                        } else {
                          console.error('Customer Details: Failed:', customerRes.responseMessage);
                          this.resetLoadingState();
                          this.toastService.error(customerRes.responseMessage || 'Failed to get customer details');
                        }
                      },
                      error: (customerError: any) => {
                        console.error('Customer Details: API error:', customerError);
                        this.resetLoadingState();

                        let errorMessage = 'Failed to get customer details. Please try again.';
                        if (customerError.error && customerError.error.responseMessage) {
                          errorMessage = customerError.error.responseMessage;
                        } else if (customerError.message) {
                          errorMessage = customerError.message;
                        }

                        this.toastService.error(errorMessage);
                      }
                    });
                  }else{
                    debugger
                    this.toastService.error(res.responseMessage);
                  }
                },
                error: (error: any) => {
                  debugger;
                  this.toastService.error(error.error.responseMessage);
                  this.isLoading = false;
                  this.cdr.detectChanges();

                }
              });
            }



          } else {
            clearTimeout(loadingTimeout);
            this.isLoading = false;
            this.cdr.detectChanges(); // Force change detection
            this.toastService.error('Failed to load client details. Please try again.');
          }
        },
        error: (error: any) => {
          clearTimeout(loadingTimeout);
          this.isLoading = false;
          this.cdr.detectChanges(); // Force change detection
          console.error('Error loading client details:', error);
          this.toastService.error('Failed to load client details. Please try again.');
        }
      });
    } else {
      this.markFormGroupTouched(this.verifyOtpForm);
    }
  }

  onForgotPasswordSubmit() {
    if (this.forgotPasswordForm.valid) {
      const formValues = this.forgotPasswordForm.value;
      const mobileNumber = formValues.mobile;

      this.isLoading = true;
      this.cdr.detectChanges(); // Force change detection
      console.log('Forgot Password: Starting forgot password process for mobile:', mobileNumber);

      // Safety timeout to prevent infinite loading
      const loadingTimeout = setTimeout(() => {
        if (this.isLoading) {
          console.warn('Forgot password request timed out, resetting loading state');
          this.resetLoadingState();
          this.toastService.error('Request timed out. Please try again.');
        }
      }, 30000); // 30 seconds timeout

      this.authService.forgotPassword(mobileNumber).subscribe({
        next: (res: ResponseModel) => {
          // Clear the timeout since request completed
          clearTimeout(loadingTimeout);
          console.log('Forgot Password: API response received:', res);

          if (res.responseCode === 'OK') {
            this.resetLoadingState();
            console.log('Forgot Password: Success - isLoading set to false');
            this.toastService.success('OTP sent successfully for password reset!');

            // Store mobile number for step 2
            localStorage.setItem('forgotPasswordMobile', mobileNumber);

            // Switch to forgot password OTP form
            this.formMode = 'forgotPasswordOtp';
            this.cdr.detectChanges(); // Force change detection to update UI immediately
          } else {
            console.error('Forgot Password: Failed:', res.responseMessage);
            this.resetLoadingState();
            console.log('Forgot Password: Failed - isLoading set to false');
            this.toastService.error(res.responseMessage || 'Failed to send OTP for password reset');
          }
        },
        error: (error: any) => {
          // Clear the timeout since request completed
          clearTimeout(loadingTimeout);

          console.error('Forgot Password: API error:', error);
          this.resetLoadingState();
          console.log('Forgot Password: API error - isLoading set to false');

          let errorMessage = 'Failed to send OTP for password reset. Please try again.';
          if (error.error && error.error.responseMessage) {
            errorMessage = error.error.responseMessage;
          } else if (error.message) {
            errorMessage = error.message;
          }

          this.toastService.error(errorMessage);
        },
        complete: () => {
          // Ensure loading is reset even if something goes wrong
          console.log('Forgot Password: Request completed, ensuring isLoading is false');
          this.resetLoadingState();
        }
      });
    } else {
      this.markFormGroupTouched(this.forgotPasswordForm);
    }
  }

  onForgotPasswordOtpSubmit() {
    debugger;
    if (this.forgotPasswordOtpForm.valid) {
      const formValues = this.forgotPasswordOtpForm.value;
      const otpValue = formValues.otp;

      // Get mobile number from storage
      const mobileNumber = localStorage.getItem('forgotPasswordMobile');

      if (!mobileNumber) {
        this.toastService.error('Mobile number not found. Please try the forgot password process again.');
        this.formMode = 'forgotPassword';
        this.cdr.detectChanges(); // Force change detection to update UI immediately
        return;
      }

      this.isLoading = true;
      this.cdr.detectChanges(); // Force change detection
      console.log('Forgot Password OTP: Verifying OTP for mobile:', mobileNumber);

      // Safety timeout to prevent infinite loading
      const loadingTimeout = setTimeout(() => {
        if (this.isLoading) {
          console.warn('OTP verification request timed out, resetting loading state');
          this.resetLoadingState();
          this.toastService.error('Request timed out. Please try again.');
        }
      }, 30000); // 30 seconds timeout

      // Load client details and verify OTP
      this.tripSearchService.loadClientDetails().subscribe({
        next: (clientDetails: ClientDetails) => {
          if (clientDetails.clientId !== null) {
            this.clientId = clientDetails.clientId;

            // Call OTP verification API
            this.authService.verifyOtpForForgotPassword(mobileNumber, otpValue, this.clientId).subscribe({
              next: (res: ResponseModel) => {
                // Clear the timeout since request completed
                clearTimeout(loadingTimeout);
                console.log('Forgot Password OTP: API response received:', res);

                if (res.responseCode === 'OK') {
                  this.isLoading = false;
                  this.cdr.detectChanges(); // Force change detection
                  console.log('Forgot Password OTP: Success - isLoading set to false');
                  this.toastService.success('OTP verified successfully!');

                  // Switch to password reset form
                  this.formMode = 'forgotPasswordStep2';
                  this.cdr.detectChanges(); // Force change detection to update UI immediately
                } else {
                  console.error('Forgot Password OTP: Failed:', res.responseMessage);
                  this.isLoading = false;
                  this.cdr.detectChanges(); // Force change detection
                  console.log('Forgot Password OTP: Failed - isLoading set to false');
                  this.toastService.error(res.responseMessage || 'OTP verification failed');
                }
              },
              error: (error: any) => {
                // Clear the timeout since request completed
                clearTimeout(loadingTimeout);

                console.error('Forgot Password OTP: API error:', error);
                this.isLoading = false;
                this.cdr.detectChanges(); // Force change detection
                console.log('Forgot Password OTP: API error - isLoading set to false');

                let errorMessage = 'OTP verification failed. Please try again.';
                if (error.error && error.error.responseMessage) {
                  errorMessage = error.error.responseMessage;
                } else if (error.error && error.error.returnMessage) {
                  errorMessage = error.error.returnMessage;
                } else if (error.message) {
                  errorMessage = error.message;
                }

                this.toastService.error(errorMessage);
              },
              complete: () => {
                // Ensure loading is reset even if something goes wrong
                console.log('Forgot Password OTP: Request completed, ensuring isLoading is false');
                if (this.isLoading) {
                  this.isLoading = false;
                  this.cdr.detectChanges(); // Force change detection
                }
              }
            });
          } else {
            clearTimeout(loadingTimeout);
            this.isLoading = false;
            this.cdr.detectChanges(); // Force change detection
            this.toastService.error('Failed to load client details. Please try again.');
          }
        },
        error: (error: any) => {
          clearTimeout(loadingTimeout);
          this.isLoading = false;
          this.cdr.detectChanges(); // Force change detection
          console.error('Error loading client details:', error);
          this.toastService.error('Failed to load client details. Please try again.');
        }
      });
    } else {
      this.markFormGroupTouched(this.forgotPasswordOtpForm);
    }
  }

  onForgotPasswordStep2Submit() {
    if (this.forgotPasswordStep2Form.valid) {
      const formValues = this.forgotPasswordStep2Form.value;
      const newPassword = formValues.password;

      // Get mobile number from storage
      const mobileNumber = localStorage.getItem('forgotPasswordMobile');

      if (!mobileNumber) {
        this.toastService.error('Mobile number not found. Please try the forgot password process again.');
        this.formMode = 'forgotPassword';
        this.cdr.detectChanges(); // Force change detection to update UI immediately
        return;
      }

      this.isLoading = true;
      this.cdr.detectChanges(); // Force change detection
      console.log('Forgot Password Step 2: Starting password reset for mobile:', mobileNumber);

      // Safety timeout to prevent infinite loading
      const loadingTimeout = setTimeout(() => {
        if (this.isLoading) {
          console.warn('Password reset request timed out, resetting loading state');
          this.resetLoadingState();
          this.toastService.error('Request timed out. Please try again.');
        }
      }, 30000); // 30 seconds timeout

      this.authService.resetPassword(mobileNumber, newPassword).subscribe({
        next: (res: ResponseModel) => {
          // Clear the timeout since request completed
          clearTimeout(loadingTimeout);
          console.log('Forgot Password Step 2: API response received:', res);

          if (res.responseCode === 'OK') {
            this.resetLoadingState();
            console.log('Forgot Password Step 2: Success - isLoading set to false');
            this.toastService.success('Password reset successfully!');

            // Clear stored mobile number
            localStorage.removeItem('forgotPasswordMobile');

            // Reset form
            this.forgotPasswordStep2Form.reset();

            // Switch back to login form
            this.formMode = 'login';
            this.cdr.detectChanges(); // Force change detection to update UI immediately
          } else {
            console.error('Forgot Password Step 2: Failed:', res.responseMessage);
            this.resetLoadingState();
            console.log('Forgot Password Step 2: Failed - isLoading set to false');
            this.toastService.error(res.responseMessage || 'Failed to reset password');
          }
        },
        error: (error: any) => {
          // Clear the timeout since request completed
          clearTimeout(loadingTimeout);

          console.error('Forgot Password Step 2: API error:', error);
          this.resetLoadingState();
          console.log('Forgot Password Step 2: API error - isLoading set to false');

          let errorMessage = 'Failed to reset password. Please try again.';
          if (error.error && error.error.responseMessage) {
            errorMessage = error.error.responseMessage;
          } else if (error.error && error.error.returnMessage) {
            errorMessage = error.error.returnMessage;
          } else if (error.message) {
            errorMessage = error.message;
          }

          this.toastService.error(errorMessage);
        },
        complete: () => {
          // Ensure loading is reset even if something goes wrong
          console.log('Forgot Password Step 2: Request completed, ensuring isLoading is false');
          this.resetLoadingState();
        }
      });
    } else {
      this.markFormGroupTouched(this.forgotPasswordStep2Form);
    }
  }

  onOtpLogin() {
    // Switch to OTP login form
    this.formMode = 'otpLogin';
  }

  onForgotPassword() {
    // Switch to forgot password form
    this.formMode = 'forgotPassword';
  }

  onBackToLogin() {
    // Go back to login form
    this.formMode = 'login';
  }

  onResendOtp() {


    this.isLoading = true;
    this.cdr.detectChanges();
    this.sendOTP();

    // Get mobile number from storage
    // const mobileNumber = localStorage.getItem('otpLoginMobile');

    // if (!mobileNumber) {
    //   this.toastService.error('Mobile number not found. Please try the OTP login process again.');
    //   this.formMode = 'otpLogin';
    //   this.cdr.detectChanges();
    //   return;
    // }

    // this.isLoading = true;
    // this.cdr.detectChanges();
    // console.log('Resend OTP: Resending OTP for mobile:', mobileNumber);

    // // Safety timeout to prevent infinite loading
    // const loadingTimeout = setTimeout(() => {
    //   if (this.isLoading) {
    //     console.warn('Resend OTP request timed out, resetting loading state');
    //     this.resetLoadingState();
    //     this.toastService.error('Request timed out. Please try again.');
    //   }
    // }, 30000); // 30 seconds timeout

    // this.authService.otpLogin(mobileNumber).subscribe({
    //   next: (res: ResponseModel) => {
    //     // Clear the timeout since request completed
    //     clearTimeout(loadingTimeout);
    //     console.log('Resend OTP: API response received:', res);

    //     if (res.responseCode === 'OK') {
    //       this.resetLoadingState();
    //       console.log('Resend OTP: Success - isLoading set to false');
    //       this.toastService.success('OTP resent successfully!');
    //     } else {
    //       console.error('Resend OTP: Failed:', res.responseMessage);
    //       this.resetLoadingState();
    //       console.log('Resend OTP: Failed - isLoading set to false');
    //       this.toastService.error(res.responseMessage || 'Failed to resend OTP');
    //     }
    //   },
    //   error: (error: any) => {
    //     // Clear the timeout since request completed
    //     clearTimeout(loadingTimeout);

    //     console.error('Resend OTP: API error:', error);
    //     this.resetLoadingState();
    //     console.log('Resend OTP: API error - isLoading set to false');

    //     let errorMessage = 'Failed to resend OTP. Please try again.';
    //     if (error.error && error.error.responseMessage) {
    //       errorMessage = error.error.responseMessage;
    //     } else if (error.message) {
    //       errorMessage = error.message;
    //     }

    //     this.toastService.error(errorMessage);
    //   },
    //   complete: () => {
    //     // Ensure loading is reset even if something goes wrong
    //     console.log('Resend OTP: Request completed, ensuring isLoading is false');
    //     this.resetLoadingState();
    //   }
    // });
  }

  onResendForgotPasswordOtp() {
    // Go back to forgot password form to resend OTP
    this.formMode = 'forgotPassword';
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }
}
